import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for handling mobile touch gestures
 * Provides swipe detection, touch feedback, and gesture utilities
 */
export const useMobileGestures = (options = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    velocityThreshold = 0.3,
  } = options;

  const touchStart = useRef({ x: 0, y: 0, time: 0 });
  const touchEnd = useRef({ x: 0, y: 0, time: 0 });

  const handleTouchStart = (e) => {
    const touch = e?.touches?.[0];
    touchStart.current = {
      x: touch?.clientX,
      y: touch?.clientY,
      time: Date.now(),
    };
  };

  const handleTouchMove = (e) => {
    const touch = e?.touches?.[0];
    touchEnd.current = {
      x: touch?.clientX,
      y: touch?.clientY,
      time: Date.now(),
    };
  };

  const handleTouchEnd = () => {
    const deltaX = touchEnd?.current?.x - touchStart?.current?.x;
    const deltaY = touchEnd?.current?.y - touchStart?.current?.y;
    const deltaTime = touchEnd?.current?.time - touchStart?.current?.time;
    
    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Horizontal swipe
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && velocityX > velocityThreshold) {
        onSwipeRight?.();
      } else if (deltaX < 0 && velocityX > velocityThreshold) {
        onSwipeLeft?.();
      }
    }
    
    // Vertical swipe
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > threshold) {
      if (deltaY > 0 && velocityY > velocityThreshold) {
        onSwipeDown?.();
      } else if (deltaY < 0 && velocityY > velocityThreshold) {
        onSwipeUp?.();
      }
    }
  };

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
};

/**
 * Custom hook for viewport detection and responsive breakpoints
 */
export const useViewport = () => {
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
    isTablet: typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth < 1024 : false,
    isDesktop: typeof window !== 'undefined' ? window.innerWidth >= 1024 : false,
    isLandscape: typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false,
    isPortrait: typeof window !== 'undefined' ? window.innerHeight >= window.innerWidth : false,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setViewport({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        isLandscape: width > height,
        isPortrait: height >= width,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewport;
};

/**
 * Custom hook for touch-optimized focus management
 */
export const useTouchFocus = () => {
  const [isTouching, setIsTouching] = useState(false);

  useEffect(() => {
    const handleTouchStart = () => setIsTouching(true);
    const handleTouchEnd = () => setIsTouching(false);
    const handleMouseDown = () => setIsTouching(false);

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  return isTouching;
};

/**
 * Custom hook for managing pull-to-refresh functionality
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const { threshold = 80, maxPullDistance = 120 } = options;
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const isRefreshing = useRef(false);

  const handleTouchStart = (e) => {
    if (window.scrollY === 0 && !isRefreshing?.current) {
      startY.current = e?.touches?.[0]?.clientY;
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling || isRefreshing?.current) return;

    const currentY = e?.touches?.[0]?.clientY;
    const distance = Math.min(currentY - startY?.current, maxPullDistance);
    
    if (distance > 0) {
      setPullDistance(distance);
      if (distance > threshold) {
        e?.preventDefault();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance > threshold && !isRefreshing?.current) {
      isRefreshing.current = true;
      await onRefresh?.();
      isRefreshing.current = false;
    }
    
    setIsPulling(false);
    setPullDistance(0);
  };

  return {
    isPulling,
    pullDistance,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
};